import AxiosInstance from '@/api/axios';
import Alert from '@/components/Base/Alert';
import Button from '@/components/Base/Button';
import { FormInput, FormTextarea } from '@/components/Base/Form';
import { Dialog } from '@/components/Base/Headless';
import Lucide from '@/components/Base/Lucide';
import { toast } from '@/components/Base/Toast/ToastManager';
import { zodResolver } from '@hookform/resolvers/zod';
import { SetStateAction, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const SupportSchema = z.object({
  subject: z.string().min(1, 'Subject is required'),
  body: z.string().min(1, 'Message is required'),
});

type SupportInputs = z.infer<typeof SupportSchema>;

const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
  'application/zip',
  'application/x-zip-compressed',
];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB per file

interface ISupportProps {
  open: boolean;
  setOpen: React.Dispatch<SetStateAction<boolean>>;
}
const Support = ({ open, setOpen }: ISupportProps) => {
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SupportInputs>({
    resolver: zodResolver(SupportSchema),
    defaultValues: { subject: '', body: '' },
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setFileError(
          'Only images (JPG, PNG, WEBP), PDF, and ZIP files are allowed.'
        );
        e.target.value = '';
        setFileNames([]);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setFileError(`"${file.name}" exceeds the 5MB size limit.`);
        e.target.value = '';
        setFileNames([]);
        return;
      }
    }

    setFileError(null);
    setFileNames(files.map((f) => f.name));
  };

  const removeFile = (index: number) => {
    const dt = new DataTransfer();
    const files = Array.from(fileInputRef.current?.files ?? []);
    files.forEach((file, i) => {
      if (i !== index) dt.items.add(file);
    });
    if (fileInputRef.current) fileInputRef.current.files = dt.files;
    setFileNames((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: SupportInputs) => {
    if (fileError) return;

    try {
      const formData = new FormData();
      formData.append('subject', data.subject);
      formData.append('body', data.body);
      formData.append('url', window.location.href);

      const files = Array.from(fileInputRef.current?.files ?? []);
      files.forEach((file) => formData.append('attachments', file));

      await AxiosInstance.post('/support', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.show({
        title: 'Support request submitted.',
        content: 'We will get back to you shortly.',
        duration: 2000,
        type: 'primary',
      });

      reset();
      setFileNames([]);
      setFileError(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      toast.show({
        title: 'Submission Failed',
        content: 'Failed to submit support request. Please try again.',
        duration: 2000,
        type: 'warning',
      });
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} size='xl'>
      <Dialog.Panel className='rounded-[0.75rem]'>
        <div className='grid grid-cols-12 gap-y-10 gap-x-6'>
          <div className='col-span-12'>
            <div className='flex flex-col gap-5 mt-3.5'>
              <div className='md:h-10 gap-y-3 flex items-center justify-between'>
                <div className='text-base font-medium group-[.mode--light]:text-white ml-5'>
                  Mail Support
                </div>
                <Button
                  variant='plain'
                  type='button'
                  className='mr-5 border-none'
                  onClick={() => setOpen(false)}
                >
                  <Lucide icon='X' />
                </Button>
              </div>
              <div className='flex flex-col'>
                <div className='p-5'>
                  <div>
                    <Alert
                      variant='outline-primary'
                      className='flex items-center px-4 py-3 mb-5 bg-primary/5 border-primary/20 rounded-[0.6rem] leading-[1.7]'
                    >
                      {({ dismiss }) => (
                        <>
                          <div className=''>
                            <Lucide
                              icon='HelpingHand'
                              className='stroke-[0.8] w-7 h-7 mr-2 fill-primary/10 primary-success'
                            />
                          </div>
                          <div className='ml-1 mr-8'>
                            We're here to help!
                            <br />
                            <span className='font-medium'>
                              Contact Support
                            </span>{' '}
                            for inquiries or any other assistance you may need.
                          </div>
                          <Alert.DismissButton
                            type='button'
                            className='btn-close'
                            onClick={dismiss}
                            aria-label='Close'
                          >
                            <Lucide icon='X' className='w-5 h-5' />
                          </Alert.DismissButton>
                        </>
                      )}
                    </Alert>
                  </div>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='mt-5 block flex-col pt-5 first:mt-0 first:pt-0 sm:flex xl:flex-row xl:items-center'>
                      <div className='mb-2 inline-block sm:mb-0 sm:mr-5 sm:text-right xl:mr-14 xl:w-60'>
                        <div className='text-left'>
                          <div className='flex items-center'>
                            <div className='font-medium'>Subject</div>
                            <div className='ml-2.5 rounded-md border px-2 py-0.5 text-xs'>
                              Required
                            </div>
                          </div>
                          <div className='mt-1.5 text-xs leading-relaxed text-slate-500/80 xl:mt-3'>
                            Brief description of your issue.
                          </div>
                        </div>
                      </div>
                      <div className='mt-3 w-full flex-1 xl:mt-0'>
                        <FormInput
                          {...register('subject')}
                          type='text'
                          placeholder='Enter subject'
                          error={errors.subject?.message}
                        />
                        {errors.subject && (
                          <p className='text-red-500 text-xs mt-1 text-left'>
                            {errors.subject.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className='mt-5 block flex-col pt-5 sm:flex xl:flex-row xl:items-center'>
                      <div className='mb-2 inline-block sm:mb-0 sm:mr-5 sm:text-right xl:mr-14 xl:w-60'>
                        <div className='text-left'>
                          <div className='flex items-center'>
                            <div className='font-medium'>Message</div>
                            <div className='ml-2.5 rounded-md border px-2 py-0.5 text-xs'>
                              Required
                            </div>
                          </div>
                          <div className='mt-1.5 text-xs leading-relaxed text-slate-500/80 xl:mt-3'>
                            Describe your issue in detail.
                          </div>
                        </div>
                      </div>
                      <div className='mt-3 w-full flex-1 xl:mt-0'>
                        <FormTextarea
                          {...register('body')}
                          placeholder='Describe your issue...'
                          className='w-full min-h-[120px]'
                        />
                        {errors.body && (
                          <p className='text-red-500 text-xs mt-1 text-left'>
                            {errors.body.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className='mt-5 block flex-col pt-5 sm:flex xl:flex-row xl:items-center'>
                      <div className='mb-2 inline-block sm:mb-0 sm:mr-5 sm:text-right xl:mr-14 xl:w-60'>
                        <div className='text-left'>
                          <div className='flex items-center'>
                            <div className='font-medium'>Attachments</div>
                            <div className='ml-2.5 rounded-md border px-2 py-0.5 text-xs'>
                              Optional
                            </div>
                          </div>
                          <div className='mt-1.5 text-xs leading-relaxed text-slate-500/80 xl:mt-3'>
                            JPG, PNG, WEBP, PDF or ZIP. Max 5MB each.
                          </div>
                        </div>
                      </div>
                      <div className='mt-3 w-full flex-1 xl:mt-0'>
                        <div className='flex items-center gap-3'>
                          <Button
                            type='button'
                            variant='outline-secondary'
                            size='sm'
                            className='h-8 pr-4 w-auto'
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Lucide icon='Paperclip' className='w-4 h-4 mr-2' />
                            Choose Files
                          </Button>
                          {fileNames.length === 0 && (
                            <span className='text-xs text-slate-500'>
                              No files chosen
                            </span>
                          )}
                        </div>

                        {fileNames.length > 0 && (
                          <div className='mt-2 flex flex-col gap-1'>
                            {fileNames.map((name, index) => (
                              <div
                                key={index}
                                className='flex items-center gap-2 text-xs text-slate-600'
                              >
                                <Lucide
                                  icon='Paperclip'
                                  className='w-3 h-3 text-slate-400'
                                />
                                <span>{name}</span>
                                <button
                                  type='button'
                                  onClick={() => removeFile(index)}
                                  className='ml-1 text-red-400 hover:text-red-600'
                                >
                                  <Lucide icon='X' className='w-3 h-3' />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        <input
                          type='file'
                          ref={fileInputRef}
                          style={{ display: 'none' }}
                          accept='image/jpeg,image/png,image/webp,application/pdf,application/zip,application/x-zip-compressed'
                          multiple
                          onChange={handleFileChange}
                        />
                        {fileError && (
                          <p className='text-red-500 text-xs mt-1 text-left'>
                            {fileError}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className='p-7 mt-6 flex border-t md:justify-end'>
                      <Button
                        variant='outline-primary'
                        type='submit'
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Request'}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};

export default Support;
